import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { loungeCopy } from "@/lib/loungeCopy";
import { LOUNGE_LANGS } from "@/lib/loungeUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";

// Editor Regole/Linee guida (per la richiesta materiale / kit), 5 lingue, lista di punti.
export function LoungeGuidelinesTab({ page, title, description }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [guidelines, setGuidelines] = useState({});

  const load = () => {
    setLoading(true);
    api.get(`/admin/lounge/settings/${page}`).then(({ data }) => {
      const merged = {};
      LOUNGE_LANGS.forEach(({ code }) => {
        const ov = (data.guidelines || {})[code];
        const def = page === "ambassador" ? loungeCopy(code).kit_guidelines || [] : [];
        merged[code] = Array.isArray(ov) ? [...ov] : [...def];
        if (merged[code].length === 0) merged[code] = [""];
      });
      setGuidelines(merged);
    }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [page]);

  const setLine = (lang, i, val) => setGuidelines((p) => ({ ...p, [lang]: p[lang].map((l, idx) => (idx === i ? val : l)) }));
  const addLine = (lang) => setGuidelines((p) => ({ ...p, [lang]: [...p[lang], ""] }));
  const removeLine = (lang, i) => setGuidelines((p) => ({ ...p, [lang]: p[lang].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(guidelines).forEach(([lang, lines]) => { clean[lang] = lines.map((l) => l.trim()).filter(Boolean); });
      await api.put(`/admin/lounge/settings/${page}`, { guidelines: clean });
      toast.success("Regole salvate");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>;

  return (
    <div className="space-y-6" data-testid="lounge-guidelines-tab">
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      <Tabs defaultValue="it">
        <TabsList>
          {LOUNGE_LANGS.map(({ code, label }) => (
            <TabsTrigger key={code} value={code} data-testid={`lounge-guideline-lang-${code}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
        {LOUNGE_LANGS.map(({ code }) => (
          <TabsContent key={code} value={code} className="mt-4 space-y-2">
            {(guidelines[code] || []).map((line, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{i + 1}.</span>
                <Input value={line} onChange={(e) => setLine(code, i, e.target.value)} data-testid={`lounge-guideline-${code}-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => removeLine(code, i)} data-testid={`lounge-guideline-remove-${code}-${i}`}><Trash2 className="h-4 w-4 text-brema-red" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => addLine(code)} data-testid={`lounge-guideline-add-${code}`}><Plus className="mr-1.5 h-4 w-4" /> Aggiungi punto</Button>
          </TabsContent>
        ))}
      </Tabs>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-brema-red text-white hover:bg-brema-red/90" data-testid="lounge-guidelines-save">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva regole
        </Button>
      </div>
    </div>
  );
}
